import SineIcon from "../../../shared-assets/icons/dataflow/generator/sine.svg";
import SquareIcon from "../../../shared-assets/icons/dataflow/generator/square.svg";
import TriangleIcon from "../../../shared-assets/icons/dataflow/generator/triangle.svg";
import AndIcon from "../../../shared-assets/icons/dataflow/logic/and.svg";
import EqualIcon from "../../../shared-assets/icons/dataflow/logic/equal.svg";
import GreaterThanOrEqualToIcon from "../../../shared-assets/icons/dataflow/logic/greater-than-or-equal-to.svg";
import GreaterThanIcon from "../../../shared-assets/icons/dataflow/logic/greater-than.svg";
import LessThanOrEqualToIcon from "../../../shared-assets/icons/dataflow/logic/less-than-or-equal-to.svg";
import LessThanIcon from "../../../shared-assets/icons/dataflow/logic/less-than.svg";
import NandIcon from "../../../shared-assets/icons/dataflow/logic/nand.svg";
import NotEqualIcon from "../../../shared-assets/icons/dataflow/logic/not-equal.svg";
import OrIcon from "../../../shared-assets/icons/dataflow/logic/or.svg";
import XorIcon from "../../../shared-assets/icons/dataflow/logic/xor.svg";
import AddIcon from "../../../shared-assets/icons/dataflow/math/add.svg";
import SubtractIcon from "../../../shared-assets/icons/dataflow/math/subtract.svg";
import MultiplyIcon from "../../../shared-assets/icons/dataflow/math/multiply.svg";
import DivideIcon from "../../../shared-assets/icons/dataflow/math/divide.svg";
import LightBulbIcon from "../../../shared-assets/icons/dataflow/output/light-bulb.svg";
import GrabberIcon from "../../../shared-assets/icons/dataflow/output/grabber.svg";
import AdvancedGrabberIcon from "../../assets/icons/output/advanced-grabber.svg";
import HumidIcon from "../../../shared-assets/icons/dataflow/output/humid.svg";
import FanIcon from "../../../shared-assets/icons/dataflow/output/fan.svg";
import TemperatureIcon from "../../../shared-assets/icons/dataflow/sensor/temperature.svg";
import CO2Icon from "../../../shared-assets/icons/dataflow/sensor/co2.svg";
import HumidityIcon from "../../../shared-assets/icons/dataflow/sensor/humidity.svg";
import LightIcon from "../../../shared-assets/icons/dataflow/sensor/light.svg";
import SignalIcon from "../../../shared-assets/icons/dataflow/control/signal.svg";
import EMGIcon from "../../../shared-assets/icons/dataflow/sensor/sensor-emg-icon.svg";
import PressureIcon from "../../../shared-assets/icons/dataflow/sensor/pressure.svg";
import ServoIcon from "../../../shared-assets/icons/dataflow/output/servo.svg";
import AbsoluteValueIcon from "../../../shared-assets/icons/dataflow/transform/absolute-value.svg";
import NegationIcon from "../../../shared-assets/icons/dataflow/transform/negation.svg";
import NotIcon from "../../../shared-assets/icons/dataflow/transform/not.svg";
import HoldPreviousArrowIcon from "../../assets/icons/control/hold-previous.svg";
import HoldCurrentArrowIcon from "../../assets/icons/control/hold-this.svg";
import HoldZeroArrowIcon from "../../assets/icons/control/hold-zero.svg";
import { isFiniteNumber } from "../../../../utilities/math-utils";
import { deviceSupportsOutput } from "./device-capabilities";
export const kMaxNodeValues = 16;
export { NodeTypes, displayNameForType } from "../../../../../shared/dataflow-node-types";
export const NodeOperationTypes = [
    {
        name: "Add",
        type: "math",
        method: (n1, n2) => n1 + n2,
        numberSentence: (n1, n2) => `${n1} + ${n2} = `,
        icon: AddIcon
    },
    {
        name: "Subtract",
        type: "math",
        method: (n1, n2) => n1 - n2,
        numberSentence: (n1, n2) => `${n1} - ${n2} = `,
        icon: SubtractIcon
    },
    {
        name: "Multiply",
        type: "math",
        method: (n1, n2) => n1 * n2,
        numberSentence: (n1, n2) => `${n1} * ${n2} = `,
        icon: MultiplyIcon
    },
    {
        name: "Divide",
        type: "math",
        method: (n1, n2) => n1 / n2,
        numberSentence: (n1, n2) => `${n1} / ${n2} = `,
        icon: DivideIcon
    },
    {
        name: "Absolute Value",
        type: "transform",
        method: (n1, n2) => Math.abs(n1),
        numberSentence: (n1, n2) => `|${n1}| = `,
        icon: AbsoluteValueIcon
    },
    {
        name: "Negation",
        type: "transform",
        method: (n1, n2) => 0 - n1,
        numberSentence: (n1, n2) => `-(${n1}) = `,
        icon: NegationIcon
    },
    {
        name: "Not",
        type: "transform",
        method: (n1, n2) => n1 ? 0 : 1,
        numberSentence: (n1, n2) => `!${n1} ⇒ `,
        icon: NotIcon
    },
    {
        name: "Round",
        type: "transform",
        method: (n1, n2) => Math.round(n1),
        numberSentence: (n1, n2) => `round(${n1}) = `,
        icon: SignalIcon
    },
    {
        name: "Floor",
        type: "transform",
        method: (n1, n2) => Math.floor(n1),
        numberSentence: (n1, n2) => `floor(${n1}) = `,
        icon: SignalIcon
    },
    {
        name: "Ceil",
        type: "transform",
        method: (n1, n2) => Math.ceil(n1),
        numberSentence: (n1, n2) => `ceil(${n1}) = `,
        icon: SignalIcon
    },
    {
        name: "Ramp",
        type: "transform",
        method: (n1, n2, prevValue) => {
            if (!isFiniteNumber(prevValue))
                return n1;
            const delta = n1 - prevValue;
            const isSteep = Math.abs(delta) > 0.1 * prevValue;
            if (delta > 0) {
                const increaseBy = n1 * 0.1;
                return isSteep ? prevValue + increaseBy : n1;
            }
            if (delta < 0) {
                const decreaseBy = prevValue * 0.1;
                return isSteep ? prevValue - decreaseBy : n1;
            }
            return n1;
        },
        numberSentence: (n1, n2) => `${n1} → `,
        icon: SignalIcon
    },
    {
        name: "Greater Than",
        type: "logic",
        method: (n1, n2) => +(n1 > n2),
        numberSentence: (n1, n2) => `${n1} > ${n2} ⇒ `,
        icon: GreaterThanIcon
    },
    {
        name: "Less Than",
        type: "logic",
        method: (n1, n2) => +(n1 < n2),
        numberSentence: (n1, n2) => `${n1} < ${n2} ⇒ `,
        icon: LessThanIcon
    },
    {
        name: "Greater Than Or Equal To",
        type: "logic",
        method: (n1, n2) => +(n1 >= n2),
        numberSentence: (n1, n2) => `${n1} >= ${n2} ⇒ `,
        icon: GreaterThanOrEqualToIcon
    },
    {
        name: "Less Than Or Equal To",
        type: "logic",
        method: (n1, n2) => +(n1 <= n2),
        numberSentence: (n1, n2) => `${n1} <= ${n2} ⇒ `,
        icon: LessThanOrEqualToIcon
    },
    {
        name: "Equal",
        type: "logic",
        method: (n1, n2) => +(n1 === n2),
        numberSentence: (n1, n2) => `${n1} == ${n2} ⇒ `,
        icon: EqualIcon
    },
    {
        name: "Not Equal",
        type: "logic",
        method: (n1, n2) => +(n1 !== n2),
        numberSentence: (n1, n2) => `${n1} != ${n2} ⇒ `,
        icon: NotEqualIcon
    },
    {
        name: "And",
        type: "logic",
        method: (n1, n2) => n1 && n2 ? 1 : 0,
        numberSentence: (n1, n2) => `${n1} && ${n2} ⇒ `,
        icon: AndIcon
    },
    {
        name: "Or",
        type: "logic",
        method: (n1, n2) => n1 || n2 ? 1 : 0,
        numberSentence: (n1, n2) => `${n1} || ${n2} ⇒ `,
        icon: OrIcon
    },
    {
        name: "Nand",
        type: "logic",
        method: (n1, n2) => +(!(n1 && n2 ? 1 : 0)),
        numberSentence: (n1, n2) => `${n1} nand ${n2} ⇒ `,
        icon: NandIcon
    },
    {
        name: "Xor",
        type: "logic",
        method: (n1, n2) => +((n1 ? 1 : 0) !== (n2 ? 1 : 0)),
        numberSentence: (n1, n2) => `${n1} xor ${n2} ⇒ `,
        icon: XorIcon
    }
];
export const HoldFunctionOptions = [
    {
        name: "Hold Current",
        displayName: "Hold this",
        type: "control",
        icon: HoldCurrentArrowIcon
    },
    {
        name: "Hold Prior",
        displayName: "Hold previous",
        type: "control",
        icon: HoldPreviousArrowIcon
    },
    {
        name: "Output Zero",
        displayName: "Hold 0",
        type: "control",
        icon: HoldZeroArrowIcon
    },
    // {
    //   name: "Hold Current Wait",
    //   displayName: "Hold this, wait",
    //   type: "control",
    //   icon: HoldCurrentArrowIcon
    // },
    // {
    //   name: "Hold Prior Wait",
    //   displayName: "Hold previous, wait",
    //   type: "control",
    //   icon: HoldPreviousArrowIcon
    // },
    // {
    //   name: "Output Zero Wait",
    //   displayName: "Hold 0, wait",
    //   type: "control",
    //   icon: HoldZeroArrowIcon
    // }
];
export const NodeSensorTypes = [
    {
        name: "Temperature",
        type: "temperature",
        units: "°C",
        icon: TemperatureIcon
    },
    {
        name: "Humidity",
        type: "humidity",
        units: "%",
        icon: HumidityIcon
    },
    {
        name: "CO₂",
        type: "CO2",
        units: "PPM",
        icon: CO2Icon
    },
    {
        name: "Light",
        type: "light",
        units: "lux",
        icon: LightIcon
    },
    // {
    //   name: "O₂",
    //   type: "O2",
    //   units: "%",
    //   icon: O2Icon
    // },
    // {
    //   name: "Soil Moisture",
    //   type: "soil-moisture",
    //   units: "",
    //   icon: MoistureIcon
    // },
    // {
    //   name: "Particulates",
    //   type: "particulates",
    //   units: "PM2.5",
    //   icon: ParticulatesIcon
    // },
    {
        name: "EMG",
        type: "emg-reading",
        units: "mV",
        icon: EMGIcon,
        decimalPlaces: 0
    },
    {
        name: "Surface Pressure",
        type: "fsr-reading",
        units: "psi",
        icon: PressureIcon,
        decimalPlaces: 0
    },
    {
        name: "Pin Reading",
        type: "pin-reading",
        units: "mV",
        icon: SignalIcon,
        decimalPlaces: 0
    }
];
export const NodeDemoOutputTypes = [
    {
        name: "Light Bulb",
        icon: LightBulbIcon
    },
    {
        name: "Grabber",
        icon: GrabberIcon,
        displayName: "Gripper"
    },
    {
        name: "Advanced Grabber",
        icon: AdvancedGrabberIcon,
        displayName: "Advanced Gripper"
    },
    {
        name: "Fan",
        icon: FanIcon
    },
    {
        name: "Humidifier",
        icon: HumidIcon
    },
    {
        name: "Servo",
        icon: ServoIcon
    }
];
export const NodeLiveOutputTypes = [
    {
        name: "Gripper 2.0",
        icon: GrabberIcon,
        angleBase: 130,
        sweep: 95
    },
    {
        name: "Grabber",
        icon: GrabberIcon,
        angleBase: 180,
        sweep: 60,
        displayName: "Gripper"
    },
    {
        name: "Humidifier",
        icon: HumidIcon,
        relayIndex: 2
    },
    {
        name: "Fan",
        icon: FanIcon,
        relayIndex: 1
    },
    {
        name: "Heat Lamp",
        icon: LightBulbIcon,
        relayIndex: 0
    },
    {
        name: "Servo",
        icon: ServoIcon,
        angleBase: 0,
        angleOffset: 30,
        angleScale: 2 / 3,
        sweep: 180
    }
];
// Resolves settings.dataflow.liveOutputTypes to the restricted list of type names, or undefined when
// the unit does not restrict outputs (absent/empty/malformed/unknown-only, or the full list). Shared
// by the node dropdown and the AI-summary mirror so both report the same set. The author's order is
// preserved (the first entry becomes the new-node default); unknown and duplicate names are dropped.
export function resolveAllowedOutputTypes(setting) {
    if (!Array.isArray(setting))
        return undefined;
    const validNames = NodeLiveOutputTypes.map(t => t.name);
    const names = setting.filter((name, i) => typeof name === "string" && validNames.includes(name) && setting.indexOf(name) === i);
    return names.length > 0 && names.length < NodeLiveOutputTypes.length ? names : undefined;
}
// These settings.dataflow.* keys are hand-authored JSON with no authoring-UI validation, so a typo
// silently degrades to the default. Warn once (from the editable tile mount) to save debugging time.
export function warnUnknownLiveOutputSettings(servoInputMode, liveOutputTypes) {
    if (servoInputMode != null && servoInputMode !== "proportion" && servoInputMode !== "degrees") {
        console.warn(`settings.dataflow.servoInputMode: ignoring unknown value ${JSON.stringify(servoInputMode)}; ` +
            `expected "degrees" or "proportion".`);
    }
    if (liveOutputTypes != null && !Array.isArray(liveOutputTypes)) {
        console.warn(`settings.dataflow.liveOutputTypes: expected an array of type names, got ` +
            `${JSON.stringify(liveOutputTypes)}; ignoring.`);
    }
    else if (Array.isArray(liveOutputTypes)) {
        const validNames = NodeLiveOutputTypes.map(t => t.name);
        const unknown = liveOutputTypes.filter(name => !validNames.includes(name));
        if (unknown.length) {
            console.warn(`settings.dataflow.liveOutputTypes: ignoring unknown type(s) ${JSON.stringify(unknown)}; ` +
                `expected names from: ${validNames.join(", ")}.`);
        }
    }
}
function createNodeMicroBitHubs(arr) {
    return arr.map((id) => {
        return {
            id,
            name: `micro:bit hub ${id}`,
            active: false
        };
    });
}
const hubIdentifiers = ["a", "b", "c", "d", "e", "f", "g", "h"];
export const NodeMicroBitHubs = createNodeMicroBitHubs(hubIdentifiers);
export const NodeGeneratorTypes = [
    {
        name: "Sine",
        method: (t, p, a) => Math.round(Math.sin(t * Math.PI / (p / 2)) * a * 100) / 100,
        icon: SineIcon
    },
    {
        name: "Square",
        method: (t, p, a) => t % p < p / 2 ? 1 * a : 0,
        icon: SquareIcon
    },
    {
        name: "Triangle",
        method: (t, p, a) => (2 * a / p) * Math.abs(t % p - p / 2),
        icon: TriangleIcon
    }
];
export const baseLiveOutputOptions = {
    liveGripperOption: {
        active: true,
        icon: GrabberIcon,
        id: "bb-gripper",
        name: "Physical Gripper"
    },
    noDeviceLiveGripperOption: {
        active: true,
        id: "no-device-bb-gripper",
        name: "Physical Gripper",
        displayName: "⚠️ connect device",
    },
    liveServoOption: {
        active: true,
        icon: ServoIcon,
        id: "bb-servo",
        name: "Physical Servo"
    },
    noDeviceliveServoOption: {
        active: true,
        id: "no-devie-bb-servo",
        name: "Physical Servo",
        displayName: "⚠️ connect device",
    },
    genericWarningOption: {
        active: true,
        id: "no-outputs-found",
        name: "⚠️ connect device",
    }
};
// Given whether a device is connected and which one, decide how a live-output
// option should present: it drives the device, the connected device can't drive
// it, or no device is connected. See design spec §3.4.
export function outputGateState(connected, deviceFamily, category) {
    if (connected && deviceSupportsOutput(deviceFamily, category))
        return "live";
    if (connected)
        return "unsupported";
    return "no-device";
}
// A hubSelect option shown when a device IS connected but does not support the
// selected output category. `name` matches the live/no-device variants so the
// hubSelect value stays stable; `displayName` carries the warning.
export function unsupportedOutputOption(category, deviceDisplayName) {
    const nameByCategory = {
        gripper: "Physical Gripper",
        servo: "Physical Servo",
        relay: "⚠️ connect device",
    };
    const label = deviceDisplayName !== null && deviceDisplayName !== void 0 ? deviceDisplayName : "This device";
    return {
        active: true,
        id: `unsupported-${category}`,
        name: nameByCategory[category],
        displayName: `⚠️ ${label} doesn't support this output`,
    };
}
export const NodePeriodUnits = [
    {
        unit: "sec",
        lengthInSeconds: 1
    },
    {
        unit: "min",
        lengthInSeconds: 60
    },
    {
        unit: "hour",
        lengthInSeconds: 3600
    },
];
export const NodeTimerInfo = {
    method: (t, tOn, tOff) => t % (tOn + tOff) < tOn ? 1 : 0,
};
export const ChartPlotColors = ["#d51eff", "#17ddd7", "#d3d114", "#3974ff", "#ff3d3d",
    "#49d381", "#b05ecb", "#ffd56d", "#ffa56d", "#f57676",
    "#fa73b0", "#eb80dc", "#cd88e4", "#d49600", "#d45200",
    "#c60e0e", "#cc0860", "#b81da1", "#8d27ad", "#8b989f",
    "#5dd581", "#3cc8f5", "#aeb9bf", "#92e3aa", "#7ad9f8",
    "#5d6e77", "#31bc5a", "#0caadd"];
export const NodePlotColor = "#3f3f3f";
export const NodePlotBlue = ChartPlotColors[3];
export const NodePlotRed = ChartPlotColors[4];
export const DEFAULT_DATA_RATE = 1000;
export const ProgramDataRates = [
    {
        text: "50ms",
        val: 50
    },
    {
        text: "100ms",
        val: 100
    },
    {
        text: "500ms",
        val: 500
    },
    {
        text: "1 sec",
        val: 1000
    },
    {
        text: "10 sec",
        val: 10000
    },
    {
        text: "1 min",
        val: 60000
    }
];
export const kSensorSelectMessage = "Select an input";
export const kSensorMissingMessage = "⚠️";
export const kAnimatedBinaryTypes = ["Fan", "Humidifier"];
export const kMicroBitHubRelaysIndexed = ["Heat Lamp", "Fan", "Humidifier"];
export const kBinaryOutputTypes = [...kMicroBitHubRelaysIndexed, "Light Bulb"];
export const kGripperOutputTypes = ["Grabber", "Gripper", "Gripper 2.0"];
export const kServoOutputTypes = ["Servo"];
//# sourceMappingURL=data:application/json;base64,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